/**
 * NIGHTBOUND - client game state.
 *
 * A single provider owns the session, the creature view, the inventory and the
 * shelter log. It never invents game state: every value it holds came from the
 * server, and every mutation goes through the API. Polling is read-only and
 * paused while the tab is hidden.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  ApiError,
  api,
  type AuthResponse,
  type CatalogResponse,
  type CreatureView,
  type InventoryEntryView,
  type UserView,
} from '../api/client'
import { audio } from '../audio/AudioManager'

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated'

export interface LogEntry {
  id: number
  message: string
  detail?: string
  effect?: string
  tone: 'neutral' | 'good' | 'bad'
  at: number
}

export type ActionKind = 'clean' | 'play' | 'sleep' | 'wake'

interface GameContextValue {
  status: SessionStatus
  user: UserView | null
  creature: CreatureView | null
  catalog: CatalogResponse | null
  inventory: InventoryEntryView[]
  log: LogEntry[]
  pending: string | null
  error: string | null
  debugEnabled: boolean
  clockOffsetMs: number

  register(input: { email: string; username: string; password: string }): Promise<boolean>
  login(input: { email: string; password: string }): Promise<boolean>
  logout(): Promise<void>
  createCreature(input: { species: string; name: string }): Promise<boolean>
  refresh(): Promise<void>
  refreshInventory(): Promise<void>
  dismissError(): void

  act(kind: ActionKind): Promise<boolean>
  feed(itemKey: string): Promise<boolean>
  train(discipline: string): Promise<boolean>
  interact(): Promise<void>
  updateSettings(patch: Partial<UserView['settings']>): Promise<void>

  devAdvance(hours: number): Promise<void>
  devSetNeeds(patch: Record<string, number>): Promise<void>
  devSetMood(mood: string): Promise<void>
  devSetSleep(sleeping: boolean): Promise<void>
  devClearCooldowns(): Promise<void>
  devResetTime(): Promise<void>
}

const GameContext = createContext<GameContextValue | null>(null)

let logCounter = 0

export function GameProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [user, setUser] = useState<UserView | null>(null)
  const [creature, setCreature] = useState<CreatureView | null>(null)
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null)
  const [inventory, setInventory] = useState<InventoryEntryView[]>([])
  const [log, setLog] = useState<LogEntry[]>([])
  const [pending, setPending] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [debugEnabled, setDebugEnabled] = useState(false)
  const [clockOffsetMs, setClockOffsetMs] = useState(0)

  const mounted = useRef(true)

  const pushLog = useCallback((entry: Omit<LogEntry, 'id' | 'at'>) => {
    logCounter += 1
    const full: LogEntry = { ...entry, id: logCounter, at: Date.now() }
    setLog((previous) => [full, ...previous].slice(0, 14))
  }, [])

  const applyCreature = useCallback((next: CreatureView | null) => {
    setCreature(next)
    // How far ahead the *server's* clock is relative to this device. Normally
    // this only corrects for clock skew, but development time-travel shifts the
    // account clock too, and every countdown must follow it. `Date.now() +`
    // this offset estimates the server's current time.
    if (next) setClockOffsetMs(new Date(next.serverTime).getTime() - Date.now())
  }, [])

  const refreshInventory = useCallback(async () => {
    try {
      const response = await api.inventory()
      if (mounted.current) setInventory(response.inventory)
    } catch {
      /* inventory is non-critical for rendering */
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      const response = await api.getCreature()
      if (mounted.current) applyCreature(response.creature)
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'NOT_FOUND') {
        if (mounted.current) applyCreature(null)
      }
    }
  }, [applyCreature])

  // ---- bootstrap ---------------------------------------------------------
  useEffect(() => {
    mounted.current = true
    const controller = new AbortController()

    ;(async () => {
      const [health, catalogResult, meResult] = await Promise.allSettled([
        api.health(),
        api.catalog(),
        api.me(),
      ])
      if (!mounted.current) return

      if (health.status === 'fulfilled') setDebugEnabled(health.value.debugTools)
      if (catalogResult.status === 'fulfilled') setCatalog(catalogResult.value)

      if (meResult.status === 'fulfilled') {
        const { user: meUser, creature: meCreature } = meResult.value
        if (meUser) {
          setUser(meUser)
          applyCreature(meCreature)
          setStatus('authenticated')
          if (meCreature) void refreshInventory()
        } else {
          // Anonymous session probe (200 with a null user).
          setStatus('anonymous')
        }
      } else if (meResult.reason instanceof ApiError && meResult.reason.status === 401) {
        // Older servers answered a missing session with 401.
        setStatus('anonymous')
      } else {
        setError('The shelter is unreachable right now.')
        setStatus('anonymous')
      }
    })()

    return () => {
      mounted.current = false
      controller.abort()
    }
  }, [applyCreature, refreshInventory])

  // ---- polling -----------------------------------------------------------
  useEffect(() => {
    if (status !== 'authenticated') return
    let timer: ReturnType<typeof setInterval> | null = null

    const start = () => {
      if (timer) return
      timer = setInterval(() => {
        void refresh()
      }, 60_000)
    }
    const stop = () => {
      if (timer) {
        clearInterval(timer)
        timer = null
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh()
        start()
      } else {
        stop()
      }
    }

    if (document.visibilityState === 'visible') start()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [status, refresh])

  // ---- auth --------------------------------------------------------------
  const adoptAuth = useCallback(
    (response: AuthResponse) => {
      setUser(response.user)
      applyCreature(response.creature)
      setStatus('authenticated')
      if (response.creature) void refreshInventory()
    },
    [applyCreature, refreshInventory],
  )

  const register = useCallback(
    async (input: { email: string; username: string; password: string }) => {
      setError(null)
      try {
        const response = await api.register(input)
        adoptAuth(response)
        return true
      } catch (cause) {
        if (cause instanceof ApiError) setError(cause.message)
        return false
      }
    },
    [adoptAuth],
  )

  const login = useCallback(
    async (input: { email: string; password: string }) => {
      setError(null)
      try {
        const response = await api.login(input)
        adoptAuth(response)
        return true
      } catch (cause) {
        if (cause instanceof ApiError) setError(cause.message)
        return false
      }
    },
    [adoptAuth],
  )

  const logout = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      /* logging out locally is enough */
    }
    setUser(null)
    applyCreature(null)
    setInventory([])
    setLog([])
    setStatus('anonymous')
    audio.stopAmbience()
  }, [applyCreature])

  const createCreature = useCallback(
    async (input: { species: string; name: string }) => {
      setError(null)
      try {
        const response = await api.createCreature(input)
        applyCreature(response.creature)
        void refreshInventory()
        pushLog({ message: `${response.creature.name} is yours now.`, tone: 'good' })
        return true
      } catch (cause) {
        if (cause instanceof ApiError) setError(cause.message)
        return false
      }
    },
    [applyCreature, pushLog, refreshInventory],
  )

  // ---- actions -----------------------------------------------------------
  const handleAction = useCallback(
    async (
      key: string,
      run: () => Promise<{ creature: CreatureView; feedback: unknown; inventory?: InventoryEntryView[] }>,
    ): Promise<boolean> => {
      setPending(key)
      setError(null)
      try {
        const response = await run()
        applyCreature(response.creature)
        if (response.inventory) setInventory(response.inventory)

        const feedback = response.feedback as {
          message?: string
          detail?: string
          effect?: string
          leveledUp?: boolean
        }
        pushLog({
          message: feedback.message ?? 'Something happens.',
          ...(feedback.detail ? { detail: feedback.detail } : {}),
          ...(feedback.effect ? { effect: feedback.effect } : {}),
          tone: feedback.leveledUp ? 'good' : 'neutral',
        })
        if (feedback.leveledUp) audio.play('level_up')
        else if (feedback.effect) audio.play('care')
        return true
      } catch (cause) {
        if (cause instanceof ApiError) {
          setError(cause.message)
          audio.play('ui_error')
        }
        return false
      } finally {
        setPending(null)
      }
    },
    [applyCreature, pushLog],
  )

  const act = useCallback(
    (kind: ActionKind) => handleAction(kind, () => api[kind]()),
    [handleAction],
  )

  const feed = useCallback(
    (itemKey: string) => handleAction('feed', () => api.feed(itemKey)),
    [handleAction],
  )

  const train = useCallback(
    (discipline: string) => handleAction('train', () => api.train(discipline)),
    [handleAction],
  )

  const interact = useCallback(async () => {
    await handleAction('interact', () => api.interact())
  }, [handleAction])

  const updateSettings = useCallback(async (patch: Partial<UserView['settings']>) => {
    try {
      const response = await api.updateSettings(patch)
      setUser(response.user)
    } catch (cause) {
      if (cause instanceof ApiError) setError(cause.message)
    }
  }, [])

  // ---- dev tools ---------------------------------------------------------
  const runDev = useCallback(
    async (key: string, run: () => Promise<{ creature: CreatureView }>) => {
      setPending(key)
      try {
        const response = await run()
        applyCreature(response.creature)
      } catch (cause) {
        if (cause instanceof ApiError) setError(cause.message)
      } finally {
        setPending(null)
      }
    },
    [applyCreature],
  )

  const value = useMemo<GameContextValue>(
    () => ({
      status,
      user,
      creature,
      catalog,
      inventory,
      log,
      pending,
      error,
      debugEnabled,
      clockOffsetMs,
      register,
      login,
      logout,
      createCreature,
      refresh,
      refreshInventory,
      dismissError: () => setError(null),
      act,
      feed,
      train,
      interact,
      updateSettings,
      devAdvance: (hours) => runDev('advance', () => api.devAdvance(hours)),
      devSetNeeds: (patch) => runDev('needs', () => api.devSetNeeds(patch)),
      devSetMood: (mood) => runDev('mood', () => api.devSetMood(mood)),
      devSetSleep: (sleeping) => runDev('sleep', () => api.devSetSleep(sleeping)),
      devClearCooldowns: () => runDev('cooldowns', () => api.devClearCooldowns()),
      devResetTime: () => runDev('reset', () => api.devResetTime()),
    }),
    [
      status,
      user,
      creature,
      catalog,
      inventory,
      log,
      pending,
      error,
      debugEnabled,
      clockOffsetMs,
      register,
      login,
      logout,
      createCreature,
      refresh,
      refreshInventory,
      act,
      feed,
      train,
      interact,
      updateSettings,
      runDev,
    ],
  )

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>
}

export function useGame(): GameContextValue {
  const context = useContext(GameContext)
  if (!context) throw new Error('useGame must be used inside <GameProvider>')
  return context
}
